<template>
  <form
    @submit.prevent="submitWithExistingListFieldValues"
    class="standard-product-revision-form uk-form-stacked"
  >
    <h2 class="uk-margin-medium">
      <router-link
        :to="{ name: 'product', params: { id: product.id } }"
      >{{product.name}}</router-link>
    </h2>

    <!-- Validity -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.validity')}}</legend>
      <div class="uk-grid-small" uk-grid>
        <div class="uk-width-1-4">
          <date-time-picker
            :disabled="!isEditable"
            :errors="fields.errors.validFrom"
            :label="$t('fields.validFrom')"
            :max="fields.values.validUntil"
            placeholder="∞"
            v-model="fields.values.validFrom"
          />
        </div>
        <div class="uk-width-1-4">
          <date-time-picker
            :disabled="!isEditable"
            :errors="fields.errors.validUntil"
            :label="$t('fields.validUntil')"
            :min="fields.values.validFrom"
            placeholder="∞"
            v-model="fields.values.validUntil"
          />
        </div>
      </div>
    </fieldset>

    <!-- Details -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.details')}}</legend>

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.securityInformationUrl"
        :label="$t('fields.securityInformationUrl')"
        :placeholder="product.securityInformationUrl"
        type="text"
        v-model="fields.values.securityInformationUrl"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.altName"
        :label="$t('fields.altName')"
        :placeholder="product.altName"
        type="text"
        v-model="fields.values.altName"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.deliveredItems"
        :label="$t('fields.deliveredItems')"
        :placeholder="product.deliveredItems"
        v-model="fields.values.deliveredItems"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.usps"
        :label="$t('fields.usps')"
        :placeholder="product.usps"
        v-model="fields.values.usps"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.summaryTexts"
        :label="$t('fields.summaryTexts')"
        :placeholder="product.summaryTexts"
        v-model="fields.values.summaryTexts"
      />

      <Checkbox v-model="fields.values.alwaysDisplayed" :label="$t('fields.alwaysDisplayed')"/>
      <Checkbox v-model="fields.values.pxsdbSecureBufferDisabled" :label="$t('fields.pxsdbSecureBufferDisabled')"/>

      <legend class="uk-legend uk-margin">{{$t('fields.tags')}}</legend>
      <inheritance-toggle
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.tags"
      >
        <keyword-list-input
          :disabled="!isEditable"
          :errors="fields.errors.tags"
          :label="$t('fields.tags')"
          v-model="fields.values.tags"
        />
      </inheritance-toggle>
    </fieldset>

     <!-- CommonCharger -->
     <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.commonCharger')}}</legend>

        <inheritance-toggle
          :overridden-default="{possible: false, included: false, minWattage: 0, maxWattage: 0, usbPd: false}"
          toggle-label="Überschreiben"
          v-model="fields.values.commonCharger"
        >
          <Checkbox
            :disabled="!isEditable"
            :errors="fields.errors.commonCharger.possible"
            :label="$t('fields.commonCharger.possible')"
            v-model="(fields.values.commonCharger || {}).possible"
          /> 

          <Checkbox
            :disabled="!isEditable"
            :errors="fields.errors.commonCharger.included"
            :label="$t('fields.commonCharger.included')"
            v-model="(fields.values.commonCharger || {}).included"
          /> 
          
          <text-input
            :disabled="!isEditable"
            :errors="fields.errors.commonCharger.minWattage"
            :label="$t('fields.commonCharger.minWattage')"
            type="number"
            v-model.number="(fields.values.commonCharger || {}).minWattage"
          />

          <text-input
            :disabled="!isEditable"
            :errors="fields.errors.commonCharger.maxWattage"
            :label="$t('fields.commonCharger.maxWattage')"
            type="number"
            v-model.number="(fields.values.commonCharger || {}).maxWattage"
          />

          <Checkbox
            :disabled="!isEditable"
            :errors="fields.errors.commonCharger.usbPd"
            :label="$t('fields.commonCharger.usbPd')"
            v-model="(fields.values.commonCharger || {}).usbPd"
          /> 

          <div slot="inherited">
            <Checkbox
              :disabled="true"
              :has-controls="false"
              :filter="{ bundle: false }"
              :label="$t('fields.commonCharger.possible')"
              v-model="product.commonCharger.possible"
            /> 

            <Checkbox
              :disabled="true"
              :has-controls="false"
              :filter="{ bundle: false }"
              :label="$t('fields.commonCharger.included')"
              v-model="product.commonCharger.included"
            /> 
 
            <text-input
              :disabled="true"
              :has-controls="false"
              :label="$t('fields.commonCharger.minWattage')"
              step="0.5"
              min="0.0"
              type="number"
              v-model.number="product.commonCharger.minWattage"
            />

          <text-input
              :disabled="true"
              :has-controls="false"
              :label="$t('fields.commonCharger.maxWattage')"
              step="0.5"
              min="0.0"
              type="number"
              v-model.number="product.commonCharger.maxWattage"
          />

          <Checkbox
            :disabled="true"
            :has-controls="false"
            :filter="{ bundle: false }"
            :label="$t('fields.commonCharger.usbPd')"
            v-model="product.commonCharger.usbPd"
          /> 
        </div> 
      </inheritance-toggle>
    </fieldset>


     <!-- Selectors -->
     <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.selectors')}}</legend>

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.size"
        :label="$t('fields.size')"
        type="text"
        v-model="fields.values.size"
      />

      <memory-input
        :disabled="!isEditable"
        :errors="fields.errors.memory"
        :label="$t('fields.memory')"
        v-model="fields.values.memory"
      />

      <memory-input
        :disabled="!isEditable"
        :errors="fields.errors.storage"
        :label="$t('fields.storage')"
        v-model="fields.values.storage"
      />
    </fieldset>

    <!-- Features -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.features')}}</legend>

      <inheritance-toggle
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.features"
      >
        <key-value-table :entries="product.features" slot="inherited" />
        <features-manager
          :disabled="!isEditable"
          :errors="fields.errors.features"
          v-model="fields.values.features"
        />
      </inheritance-toggle>
    </fieldset>

    <!-- SEO -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.seo')}}</legend>

      <textarea-input
        :disabled="!isEditable"
        :errors="fields.errors.description"
        :label="$t('fields.description')"
        :placeholder="product.description"
        :rows="5"
        v-model="fields.values.description"
      />

      <input-inheritance-toggle
        :label="$t('fields.keywords')"
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.keywords"
      >
        <keyword-list-input
          :disabled="!isEditable"
          :errors="fields.errors.keywords"
          :label="$t('fields.keywords')"
          v-model="fields.values.keywords"
        />
        <div slot="inherited">
          <separated-list :items="product.keywords" />
        </div>
      </input-inheritance-toggle>
    </fieldset>

    <!-- Legal Texts -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.legalTexts')}}</legend>

      <inheritance-toggle
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.legalTexts"
      >
        <legal-text-manager
          :disabled="!isEditable"
          :errors="fields.errors.legalTexts"
          v-model="fields.values.legalTexts"
        />
        <legal-text-manager
          :has-controls="false"
          :value="product.legalTexts"
          slot="inherited"
        />
      </inheritance-toggle>
    </fieldset>

    <!-- Media -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.media')}}</legend>

      <inheritance-toggle
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.media"
      >
        <attachment-manager
          :disabled="!isEditable"
          :errors="fields.errors.media"
          attachment-type="MEDIA_OBJECT"
          v-model="fields.values.media"
        />
        <attachment-manager
          :has-controls="false"
          :value="product.media"
          slot="inherited"
        />
      </inheritance-toggle>
    </fieldset>

    <!-- Disable Stock Update -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <div class="uk-margin-small uk-margin-small-left">
        <p class="uk-margin-remove">Wenn aktiv, wird der Bestand für dieses Produkt, während des PXSDB Imports, im angegebenen Zeitraum nicht importiert/synchronisiert.</p>
        <p class="uk-margin-remove">Ein manueller Bestand kann hier eingegeben/angepasst werden: <a :href="productAvailabilityRoute">Produkt &gt; Verfügbarkeit</a></p>
      </div>
      <legend class="uk-legend uk-margin-top">{{$t('fieldsets.skipStockAvailabilityImport')}}</legend>
      <Checkbox v-model="fields.values.stockSkipUpdate" :label="$t('fields.stockSkipUpdate')"/>
      <div  v-if="fields.values.stockSkipUpdate" div class="uk-grid-small" uk-grid>
        <div class="uk-width-1-4">
          <date-time-picker
            :disabled="!isEditable"
            :errors="fields.errors.stockSkipUpdateFrom"
            :label="$t('fields.stockSkipUpdateFrom')"
            :max="fields.values.stockSkipUpdateTo"
            :required="fields.values.stockSkipUpdate"
            placeholder="∞"
            v-model="fields.values.stockSkipUpdateFrom"
          />
        </div>
        <div class="uk-width-1-4">
          <date-time-picker
            :disabled="!isEditable"
            :errors="fields.errors.stockSkipUpdateTo"
            :label="$t('fields.stockSkipUpdateTo')"
            :min="fields.values.stockSkipUpdateFrom"
            :required="fields.values.stockSkipUpdate"
            placeholder="∞"
            v-model="fields.values.stockSkipUpdateTo"
          />
        </div>
      </div>

    </fieldset>
    <div class="uk-margin-medium-top">
      <submit-button :disabled="!isSubmittable" type="primary" />
      <cancel-button />
    </div>
  </form>
</template>

<script>
import AttachmentManager from '@/components/AttachmentManager';
import CancelButton from '@/components/CancelButton';
import Checkbox from '@/components/Checkbox';
import DateTimePicker from '@/components/DateTimePicker';
import FeaturesManager from '@/components/FeaturesManager';
import Form from '@/mixins/Form';
import ProductRevisionFormHelper from '@/mixins/ProductRevisionFormHelper';
import InheritanceToggle from '@/components/InheritanceToggle';
import InputInheritanceToggle from '@/components/InputInheritanceToggle';
import KeyValueTable from '@/components/KeyValueTable';
import LegalTextManager from '@/components/LegalTextManager';
import KeywordListInput from '@/components/KeywordListInput';
import SeparatedList from '@/components/SeparatedList';
import SubmitButton from '@/components/SubmitButton';
import TextareaInput from '@/components/TextareaInput';
import TextInput from '@/components/TextInput';
import TextListInput from '@/components/TextListInput';
import MemoryInput from '@/components/MemoryInput';

export default {
  name: 'standard-product-revision-form',
  mixins: [
    Form({
      altName: true,
      commonCharger: { type: Form.Types.Object, default: null },
      deliveredItems: { type: Form.Types.NullableArray },
      description: true,
      features: { type: Form.Types.NullableArray },
      keywords: { type: Form.Types.NullableArray },
      legalTexts: { type: Form.Types.ReferenceList, default: null },
      media: {
        type: Form.Types.ReferenceList,
        default: null,
        dumpTo: 'mediaObjectIds',
      },
      summaryTexts: { type: Form.Types.NullableArray },
      usps: { type: Form.Types.NullableArray },
      validFrom: true,
      validUntil: true,
      alwaysDisplayed: true,
      pxsdbSecureBufferDisabled: true,
      securityInformationUrl: true,
      tags: { type: Form.Types.NullableArray },
      storage: { type: Form.Types.NullableObject },
      size: true,
      memory: { type: Form.Types.NullableObject },
      stockSkipUpdate: true,
      stockSkipUpdateFrom: true,
      stockSkipUpdateTo: true,
    }),
    ProductRevisionFormHelper({ listFields: ['tags', 'media'] }),
  ],
  components: {
    AttachmentManager,
    CancelButton,
    Checkbox,
    DateTimePicker,
    FeaturesManager,
    KeyValueTable,
    LegalTextManager,
    InheritanceToggle,
    InputInheritanceToggle,
    KeywordListInput,
    SeparatedList,
    SubmitButton,
    TextareaInput,
    TextInput,
    TextListInput,
    MemoryInput,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    if (!this.fields.values.alwaysDisplayed) {
      this.fields.values.alwaysDisplayed = false;
    }
    if (!this.fields.values.stockSkipUpdate) {
      this.fields.values.stockSkipUpdate = false;
    }
    if (!this.fields.values.pxsdbSecureBufferDisabled) {
      this.fields.values.pxsdbSecureBufferDisabled = false;
    }
    this.handleRevisionListFieldDiff();
  },
  computed: {
    productAvailabilityRoute() {
      return `/products/${this.product.id}/availabilities`;
    },
  },
  watch: {
    'fields.values.stockSkipUpdate': {
      handler(newValue) {
        // mark the fields as required if stocSkipUpdate is enabled
        this.definitions.stockSkipUpdateFrom.required = newValue;
        this.definitions.stockSkipUpdateTo.required = newValue;

        // reset the values if stocSkipUpdate is disabled
        if (!newValue) {
          this.fields.values.stockSkipUpdateFrom = null;
          this.fields.values.stockSkipUpdateTo = null;
        }

        // set default value for stockSkipUpdateFrom to now
        if (newValue && !this.fields.values.stockSkipUpdateFrom
            && !this.fields.values.stockSkipUpdateTo) {
          this.fields.values.stockSkipUpdateFrom = new Date().toISOString();
        }
      },
      immediate: true,
    },
  },
};
</script>



// WEBPACK FOOTER //
// StandardProductRevisionForm.vue?487bcd4c